import * as yup from 'yup';
import { regExIdentifiers, validationMessages } from 'constants/validations';
import {
    Attribute,
    DictionaryAttribute,
    DictionaryIndex,
    IDataType,
    numericSchemaTest,
} from '../../../lib';
import { themeObject as theme } from 'lib/util/themes/godzilla';
import { validationsMessages } from 'lib';
import { TabItemsType } from 'components/landing-page-wrapper/components/TabComponent';

export type DictionaryTabs = 'Details' | 'Attributes' | 'Indexes';

export const dictionaryTabs: TabItemsType[] = [
    { label: 'Details', isError: false },
    { label: 'Attributes', isError: false },
    { label: 'Indexes', isError: false },
];

export const DetailsTabsErrorMessage =
    'Please enter all required fields on Details tab.';
export const AttributesTabsErrorMessage =
    'Object and Object[] Attributes require at least one child item.';

export const dictionaryDetailSchema = yup.object().shape({
    name: yup
        .string()
        .required(validationsMessages.required)
        .matches(regExIdentifiers.name, validationMessages.invalidName),
    description: yup.string().optional(),
    groupName: yup.string().optional(),
    undefinedAttributesAction: yup.string().optional(),
    properties: yup.array().optional(),
});

export const dictionaryDetailsDefaults = {
    name: '',
    description: '',
    groupName: '',
    undefinedAttributesAction: 'KEEP',
    nodeColorAction: '',
};

const getFormattedDataType = (
    value: string,
    label: string,
    group: string
): IDataType => {
    return {
        value,
        label,
        group,
    };
};

export const DATA_TYPE_GROUPS = {
    STRING: 'string',
    NUMBER: 'number',
    BOOLEAN: 'boolean',
    DECIMAL: 'decimal',
    DATE: 'date',
    DATETIME: 'datetime',
    FINGERPRINT: 'fingerprint',
    ENUM: 'enum',
    BLOB: 'blob',
    OBJECT: 'object',
    CLOB: 'clob',
    STRING_REFERENCE: 'string_reference',
    STRING_REFERENCE_ARRAY: 'string_reference_array',
    INTEGER_REFERENCE: 'integer_reference',
    INTEGER_REFERENCE_ARRAY: 'integer_reference_array',
    LONG_REFERENCE: 'long_reference',
    LONG_REFERENCE_ARRAY: 'long_reference_array',
    VECTOR: 'vector',
};

export const DATA_TYPE_VALUES = {
    STRING: 'STRING',
    INTEGER: 'INTEGER',
    LONG: 'LONG',
    BOOLEAN: 'BOOLEAN',
    DECIMAL: 'DECIMAL',
    DATE: 'DATE',
    DATETIME: 'DATETIME',
    FINGERPRINT: 'FINGERPRINT',
    ENUM: 'ENUM',
    BLOB: 'BLOB',
    OBJECT: 'OBJECT',
    CLOB: 'CLOB',
    STRING_ARRAY: 'STRING[]',
    INTEGER_ARRAY: 'INTEGER[]',
    LONG_ARRAY: 'LONG[]',
    BOOLEAN_ARRAY: 'BOOLEAN[]',
    DECIMAL_ARRAY: 'DECIMAL[]',
    DATE_ARRAY: 'DATE[]',
    DATETIME_ARRAY: 'DATETIME[]',
    ENUM_ARRAY: 'ENUM[]',
    BLOB_ARRAY: 'BLOB[]',
    OBJECT_ARRAY: 'OBJECT[]',
    STRING_REFERENCE: 'STRING_REFERENCE',
    STRING_REFERENCE_ARRAY: 'STRING_REFERENCE[]',
    INTEGER_REFERENCE: 'INTEGER_REFERENCE',
    INTEGER_REFERENCE_ARRAY: 'INTEGER_REFERENCE[]',
    LONG_REFERENCE: 'LONG_REFERENCE',
    LONG_REFERENCE_ARRAY: 'LONG_REFERENCE[]',
    VECTOR: 'VECTOR',
};

export const DATA_TYPE_LABELS = {
    STRING: DATA_TYPE_VALUES.STRING,
    INTEGER: DATA_TYPE_VALUES.INTEGER,
    LONG: DATA_TYPE_VALUES.LONG,
    BOOLEAN: DATA_TYPE_VALUES.BOOLEAN,
    DECIMAL: DATA_TYPE_VALUES.DECIMAL,
    DATE: DATA_TYPE_VALUES.DATE,
    DATETIME: DATA_TYPE_VALUES.DATETIME,
    FINGERPRINT: DATA_TYPE_VALUES.FINGERPRINT,
    ENUM: DATA_TYPE_VALUES.ENUM,
    BLOB: DATA_TYPE_VALUES.BLOB,
    OBJECT: DATA_TYPE_VALUES.OBJECT,
    CLOB: DATA_TYPE_VALUES.CLOB,
    STRING_ARRAY: 'STRING_ARRAY',
    INTEGER_ARRAY: 'INTEGER_ARRAY',
    LONG_ARRAY: 'LONG_ARRAY',
    BOOLEAN_ARRAY: 'BOOLEAN_ARRAY',
    DECIMAL_ARRAY: 'DECIMAL_ARRAY',
    DATE_ARRAY: 'DATE_ARRAY',
    DATETIME_ARRAY: 'DATETIME_ARRAY',
    ENUM_ARRAY: 'ENUM_ARRAY',
    BLOB_ARRAY: 'BLOB_ARRAY',
    OBJECT_ARRAY: 'OBJECT_ARRAY',
    STRING_REFERENCE: 'STRING_REFERENCE',
    STRING_REFERENCE_ARRAY: 'STRING_REFERENCE_ARRAY',
    INTEGER_REFERENCE: 'INTEGER_REFERENCE',
    INTEGER_REFERENCE_ARRAY: 'INTEGER_REFERENCE_ARRAY',
    LONG_REFERENCE: 'LONG_REFERENCE',
    LONG_REFERENCE_ARRAY: 'LONG_REFERENCE_ARRAY',
    VECTOR: DATA_TYPE_VALUES.VECTOR,
};

export const dataTypes: IDataType[] = [
    getFormattedDataType(
        DATA_TYPE_LABELS.STRING,
        DATA_TYPE_VALUES.STRING,
        DATA_TYPE_GROUPS.STRING
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.INTEGER,
        DATA_TYPE_VALUES.INTEGER,
        DATA_TYPE_GROUPS.NUMBER
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.LONG,
        DATA_TYPE_VALUES.LONG,
        DATA_TYPE_GROUPS.NUMBER
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.BOOLEAN,
        DATA_TYPE_VALUES.BOOLEAN,
        DATA_TYPE_GROUPS.BOOLEAN
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.DECIMAL,
        DATA_TYPE_VALUES.DECIMAL,
        DATA_TYPE_GROUPS.DECIMAL
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.DATE,
        DATA_TYPE_VALUES.DATE,
        DATA_TYPE_GROUPS.DATE
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.DATETIME,
        DATA_TYPE_VALUES.DATETIME,
        DATA_TYPE_GROUPS.DATETIME
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.FINGERPRINT,
        DATA_TYPE_VALUES.FINGERPRINT,
        DATA_TYPE_GROUPS.FINGERPRINT
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.ENUM,
        DATA_TYPE_VALUES.ENUM,
        DATA_TYPE_GROUPS.ENUM
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.BLOB,
        DATA_TYPE_VALUES.BLOB,
        DATA_TYPE_GROUPS.BLOB
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.OBJECT,
        DATA_TYPE_VALUES.OBJECT,
        DATA_TYPE_GROUPS.OBJECT
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.CLOB,
        DATA_TYPE_VALUES.CLOB,
        DATA_TYPE_GROUPS.CLOB
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.STRING_ARRAY,
        DATA_TYPE_VALUES.STRING_ARRAY,
        DATA_TYPE_GROUPS.STRING
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.INTEGER_ARRAY,
        DATA_TYPE_VALUES.INTEGER_ARRAY,
        DATA_TYPE_GROUPS.NUMBER
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.LONG_ARRAY,
        DATA_TYPE_VALUES.LONG_ARRAY,
        DATA_TYPE_GROUPS.NUMBER
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.BOOLEAN_ARRAY,
        DATA_TYPE_VALUES.BOOLEAN_ARRAY,
        DATA_TYPE_GROUPS.BOOLEAN
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.DECIMAL_ARRAY,
        DATA_TYPE_VALUES.DECIMAL_ARRAY,
        DATA_TYPE_GROUPS.DECIMAL
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.DATE_ARRAY,
        DATA_TYPE_VALUES.DATE_ARRAY,
        DATA_TYPE_GROUPS.DATE
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.DATETIME_ARRAY,
        DATA_TYPE_VALUES.DATETIME_ARRAY,
        DATA_TYPE_GROUPS.DATETIME
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.ENUM_ARRAY,
        DATA_TYPE_VALUES.ENUM_ARRAY,
        DATA_TYPE_GROUPS.ENUM
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.BLOB_ARRAY,
        DATA_TYPE_VALUES.BLOB_ARRAY,
        DATA_TYPE_GROUPS.BLOB
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.OBJECT_ARRAY,
        DATA_TYPE_VALUES.OBJECT_ARRAY,
        DATA_TYPE_GROUPS.OBJECT
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.STRING_REFERENCE,
        DATA_TYPE_VALUES.STRING_REFERENCE,
        DATA_TYPE_GROUPS.STRING_REFERENCE
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.STRING_REFERENCE_ARRAY,
        DATA_TYPE_VALUES.STRING_REFERENCE_ARRAY,
        DATA_TYPE_GROUPS.STRING_REFERENCE_ARRAY
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.INTEGER_REFERENCE,
        DATA_TYPE_VALUES.INTEGER_REFERENCE,
        DATA_TYPE_GROUPS.INTEGER_REFERENCE
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.INTEGER_REFERENCE_ARRAY,
        DATA_TYPE_VALUES.INTEGER_REFERENCE_ARRAY,
        DATA_TYPE_GROUPS.INTEGER_REFERENCE_ARRAY
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.LONG_REFERENCE,
        DATA_TYPE_VALUES.LONG_REFERENCE,
        DATA_TYPE_GROUPS.LONG_REFERENCE
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.LONG_REFERENCE_ARRAY,
        DATA_TYPE_VALUES.LONG_REFERENCE_ARRAY,
        DATA_TYPE_GROUPS.LONG_REFERENCE_ARRAY
    ),
    getFormattedDataType(
        DATA_TYPE_LABELS.VECTOR,
        DATA_TYPE_VALUES.VECTOR,
        DATA_TYPE_GROUPS.VECTOR
    ),
];

export const getDTGroup = (dataType: string): string | undefined => {
    return dataTypes.find((x) => x.value === dataType)?.group;
};

export const attributeSchema: yup.SchemaOf<Attribute> = yup.object({
    id: yup.string().optional(),
    displayName: yup.string().optional(),
    dynamicName: yup.string().optional(),
    treeViewDisplayName: yup.string().optional(),
    name: yup
        .string()
        .required(validationsMessages.required)
        .matches(regExIdentifiers.name, validationMessages.invalidName),
    description: yup.string().optional(),
    datatype: yup.string().required(validationsMessages.required),
    required: yup.boolean().optional(),
    defaultValue: yup.string().optional(),
    min: yup.mixed().when('datatype', (datatype) => {
        return getDTGroup(datatype) === DATA_TYPE_GROUPS.NUMBER
            ? numericSchemaTest(
                  validationMessages.invalidNumber,
                  regExIdentifiers.numbers
              )
            : numericSchemaTest(
                  validationMessages.invalidDecimal,
                  regExIdentifiers.decimals
              );
    }),
    max: yup.mixed().when('datatype', (datatype) => {
        return getDTGroup(datatype) === DATA_TYPE_GROUPS.NUMBER
            ? numericSchemaTest(
                  validationMessages.invalidNumber,
                  regExIdentifiers.numbers
              )
            : numericSchemaTest(
                  validationMessages.invalidDecimal,
                  regExIdentifiers.decimals
              );
    }),
    precision: numericSchemaTest(
        validationMessages.precision,
        regExIdentifiers.precision
    ),
    referenceDictionaryName: yup
        .string()
        .required(validationsMessages.required),
    enumValues: yup.array().of(yup.string()).optional(),
    regex: yup.string().optional(),
    error: yup.boolean().optional(),
    fingerprintAttributes: yup.array().optional(),
});

// This is part of crosswalk, should be moved, SHOULD BE exported
// also needs to be renamed
const removeCrosswalkItem = (attribute: any): void => {
    delete attribute.id;
    if (Array.isArray(attribute.attributes)) {
        for (const item of attribute.attributes) {
            removeCrosswalkItem(item);
        }
    }

    if (attribute.nodeAttributes) {
        for (const item of attribute.nodeAttributes) {
            removeCrosswalkItem(item);
        }
    }
};
// This is part of crosswalk, should be moved, and NOT exported
const removeCrosswalk = (
    items: any[]
): DictionaryAttribute[] | DictionaryIndex[] => {
    for (const itemsMap of items) {
        removeCrosswalkItem(itemsMap);
    }

    return items;
};

export const prepForSave = (
    itemList: any[]
): DictionaryAttribute[] | DictionaryIndex[] => {
    const items = structuredClone(itemList);
    return removeCrosswalk(items);
};

export const newIndex = (): {
    id: string;
    name: string;
    attributes: [];
    type: string;
} => {
    return {
        id: '',
        name: 'new index',
        attributes: [],
        type: 'NORMAL',
    };
};

export const styles = {
    dictionaryHeading: {
        mt: 3,
        fontWeight: 600,
    },
    editAttributeHeading: {
        fontWeight: '600',
        fontFamily: 'Raleway',
        fontSize: '28px',
        color: '#293541',
    },
    visible: {
        display: 'initial',
    },
    hidden: {
        display: 'hidden',
    },
    dictionaryActions: {
        display: 'flex',
        justifyContent: 'end',
        '& Button': {
            mr: 2,
        },
    },
    saveButton: {
        my: 'auto',
    },
    textFields: {
        '& .MuiInputBase-input': {
            padding: '8px 10px !important',
            stroke: theme.palette.grayScale[700],
        },
    },
    tabsStyles: {
        '& .MuiTabs-scroller': {
            '& .MuiTabs-flexContainer': {
                height: '32px',
            },
        },
    },
    tabItemStyles: {
        minHeight: '0',
        height: '32px',
        m: '9px 6px !important',
    },
    renderTabComponentContainerStyles: {
        mt: '12px',
        height: '100%',
    },
    menuStyles: '251px',
};

export const newDictionaryDetails = {
    name: '',
    description: '',
    groupName: '',
    undefinedAttributesAction: 'KEEP',
};

export const newDictionaryAttributes: DictionaryAttribute[] = [];
export const newDictionaryIndexes: DictionaryIndex[] = [];

export const newDictionary = {
    ...newDictionaryDetails,
    attributes: newDictionaryAttributes,
    indexes: newDictionaryIndexes,
};

export const dictionaryToastError = {
    duplicatedDictionary: {
        msgTitle: 'Dictionary Not Saved',
        messageText: 'A dictionary with this name already exists',
    },
    missingAttributes: {
        msgTitle: 'Dictionary Not Saved',
        messageText: 'Dictionary must contain at least one attribute',
    },
};
